import { useState, useRef, useCallback, useEffect } from "react";
import { useToast } from "@/shared/hooks/use-toast";
import type { Socket } from "socket.io-client";
import { showImportantToast } from "@/features/videocall/components/components/Videocalltoasts";
import { apiClient } from "@/shared/services/api";

// mediasoup can still use client-side dynamic recorder; server-side recorder is optional.
const USE_MEDIASOUP = import.meta.env.VITE_USE_MEDIASOUP === "true";
const USE_MEDIASOUP_SERVER_RECORDING =
  USE_MEDIASOUP && import.meta.env.VITE_MEDIASOUP_SERVER_RECORDING === "true";

// Chunk size for large uploads (5MB)
const CHUNK_SIZE = 5 * 1024 * 1024;
// Size threshold for chunked upload (50MB - anything larger uses chunked upload)
const CHUNKED_UPLOAD_THRESHOLD = 50 * 1024 * 1024;
// Retry config for uploads (handles transient network errors)
const UPLOAD_MAX_RETRIES = 3;
const UPLOAD_RETRY_DELAY_MS = 2000;

interface UseRecordingProps {
  roomId: string;
  isHost: boolean;
  socketRef: React.MutableRefObject<Socket | null>;
  remoteVideoRef: React.RefObject<HTMLVideoElement>;
  localVideoRef: React.RefObject<HTMLVideoElement>;
  localStreamRef: React.MutableRefObject<MediaStream | null>;
  baseUrl: string;
  /** Host's device orientation angle (0,90,180,270) for recording canvas */
  hostOrientationRef?: React.MutableRefObject<number>;
  /** Remote (guest) orientation angle for recording canvas */
  remoteOrientationRef?: React.MutableRefObject<number>;
}

export function useRecording({
  roomId,
  isHost,
  socketRef,
  remoteVideoRef,
  localVideoRef,
  localStreamRef,
  baseUrl,
  hostOrientationRef,
  remoteOrientationRef,
}: UseRecordingProps) {
  const { toast } = useToast();
  const [isRecording, setIsRecording] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null); // null = not uploading, 0-100 = progress
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingStartRef = useRef<number | null>(null);
  const mixCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawAnimRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const mixedStreamRef = useRef<MediaStream | null>(null);
  const initialCanvasDimensionsRef = useRef<{ width: number; height: number } | null>(null); // Lock canvas size during recording
  const recordingNumberRef = useRef<number>(0); // Track recording number for this session

  // Stabilize remote dimensions across reconnects
  const lastKnownRemoteDimsRef = useRef<{ w: number; h: number } | null>(null);
  const remoteDimsStableFramesRef = useRef<number>(0);
  // Track the candidate dims we're trying to stabilize (separate from committed)
  const candidateRemoteDimsRef = useRef<{ w: number; h: number } | null>(null);
  
  // Persist a locked recording canvas size across host refresh/rejoin
  const lockedCanvasDimsRef = useRef<{ width: number; height: number } | null>(null);
  

  // ✅ FIX: Restore recording number and isRecording from sessionStorage on mount
  // Prevents reset on host refresh; server-side recording continues when host reconnects
  useEffect(() => {
    const storedNum = sessionStorage.getItem(`recording_num_${roomId}`);
    if (storedNum) {
      recordingNumberRef.current = parseInt(storedNum, 10);
      console.log(`📹 [Recording] Restored recording number: ${recordingNumberRef.current}`);
    }
    const storedCanvas = sessionStorage.getItem(`recording_canvas_${roomId}`);
    if (storedCanvas) {
      try {
        lockedCanvasDimsRef.current = JSON.parse(storedCanvas);
        console.log("📹 [Recording] Restored locked canvas:", lockedCanvasDimsRef.current);
      } catch {}
    }
    if (USE_MEDIASOUP_SERVER_RECORDING && sessionStorage.getItem(`recording_active_${roomId}`) === 'true') {
      setIsRecording(true);
      console.log('📹 [Recording] Restored isRecording=true (server-side recording continues)');
    }
  }, [roomId]);

  /**
   * Upload a large blob in chunks using XMLHttpRequest for progress tracking
   * This is more reliable for large files (30+ minute recordings)
   */
  const uploadBlobWithProgress = async (
    signedUrl: string,
    blob: Blob,
    contentType: string,
    onProgress: (percent: number) => void
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
          console.log(`📤 Upload progress: ${percent}% (${(event.loaded / 1024 / 1024).toFixed(1)}MB / ${(event.total / 1024 / 1024).toFixed(1)}MB)`);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log('✅ Upload completed successfully');
          resolve(true);
        } else {
          console.error(`❌ Upload failed with status ${xhr.status}: ${xhr.responseText}`);
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        console.error('❌ Upload network error');
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        console.error('❌ Upload aborted');
        reject(new Error('Upload was aborted'));
      });

      // Set a long timeout for large files (30 minutes)
      xhr.timeout = 30 * 60 * 1000;
      xhr.addEventListener('timeout', () => {
        console.error('❌ Upload timeout after 30 minutes');
        reject(new Error('Upload timed out after 30 minutes'));
      });

      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(blob);
    });
  };

  /**
   * Upload with retries - handles transient network errors (e.g. connection drops at 30%)
   * Retries up to UPLOAD_MAX_RETRIES times with delay between attempts.
   */
  const uploadWithRetries = async (
    signedUrl: string,
    blob: Blob,
    contentType: string,
    onProgress: (percent: number) => void
  ): Promise<boolean> => {
    let lastError: Error | null = null;
    for (let attempt = 1; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
      try {
        if (attempt > 1) {
          console.log(`📤 Retry attempt ${attempt}/${UPLOAD_MAX_RETRIES} after network error...`);
          await new Promise((r) => setTimeout(r, UPLOAD_RETRY_DELAY_MS));
        }
        await uploadBlobWithProgress(signedUrl, blob, contentType, onProgress);
        return true;
      } catch (e: any) {
        lastError = e;
        const isRetryable =
          e?.message?.includes('Network error') ||
          e?.message?.includes('timed out') ||
          e?.message?.includes('aborted');
        if (!isRetryable || attempt === UPLOAD_MAX_RETRIES) {
          throw e;
        }
        console.warn(`⚠️ Upload attempt ${attempt} failed (${e?.message}), retrying...`);
      }
    }
    throw lastError || new Error('Upload failed after retries');
  };

  const buildMixedStream = () => {
    const remoteEl = remoteVideoRef.current;
    const localEl = localVideoRef.current;

    // ✅ CRITICAL FIX: Validate remote video element is ready before building stream
    if (!remoteEl) {
      console.error('❌ [Recording] Remote video element not available');
      return null;
    }

    if (!remoteEl.srcObject) {
      console.error('❌ [Recording] Remote video has no srcObject');
      return null;
    }

    if (remoteEl.readyState < 2) {
      console.error('❌ [Recording] Remote video not ready (readyState < 2):', {
        readyState: remoteEl.readyState,
        networkState: remoteEl.networkState,
        videoWidth: remoteEl.videoWidth,
        videoHeight: remoteEl.videoHeight
      });
      return null;
    }

    if (!remoteEl.videoWidth || !remoteEl.videoHeight) {
      console.error('❌ [Recording] Remote video dimensions not available:', {
        videoWidth: remoteEl.videoWidth,
        videoHeight: remoteEl.videoHeight,
        readyState: remoteEl.readyState
      });
      return null;
    }

    console.log('✅ [Recording] Remote video validated:', {
      videoWidth: remoteEl.videoWidth,
      videoHeight: remoteEl.videoHeight,
      readyState: remoteEl.readyState
    });

    // Choose canvas size once, not based on transient widths
    const persisted = lockedCanvasDimsRef.current;
    let W = persisted?.width ?? remoteEl.videoWidth;
    let H = persisted?.height ?? remoteEl.videoHeight;

    // Enforce minimum recording resolution of 1280x720 for good desktop playback
    // Scale up proportionally if the remote video is smaller than HD
    const MIN_WIDTH = 1280;
    const MIN_HEIGHT = 720;
    if (W < MIN_WIDTH && H < MIN_HEIGHT) {
      const scale = Math.max(MIN_WIDTH / W, MIN_HEIGHT / H);
      W = Math.round(W * scale);
      H = Math.round(H * scale);
      console.log(`📹 [Recording] Upscaled canvas from ${remoteEl.videoWidth}x${remoteEl.videoHeight} to ${W}x${H} for desktop playback`);
    }

    // Lock AFTER upscaling so reconnects use the final resolution
    if (!persisted) {
      lockedCanvasDimsRef.current = { width: W, height: H };
      sessionStorage.setItem(`recording_canvas_${roomId}`, JSON.stringify({ width: W, height: H }));
      console.log(`📹 [Recording] 🔒 Persisted canvas size: ${W}x${H}`);
    }

    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    mixCanvasRef.current = canvas;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.error('❌ [Recording] Failed to get canvas 2D context');
      return null;
    }

    // Keep a backup ImageData of the last good frame so we can hold it
    // during momentary video interruptions (e.g. camera flip)
    let lastGoodFrame: ImageData | null = null;

    const draw = () => {
      try {
        const rawW = remoteEl?.videoWidth || 0;
        const rawH = remoteEl?.videoHeight || 0;

        // Only accept "reasonable" dims; reject tiny/zero glitch values
        // Use low thresholds since mobile devices can send 320x240
        const reasonable =
          rawW >= 160 && rawH >= 120 && rawW / rawH > 0.5 && rawW / rawH < 3.0;

        if (reasonable) {
          const prevCandidate = candidateRemoteDimsRef.current;

          if (prevCandidate && prevCandidate.w === rawW && prevCandidate.h === rawH) {
            remoteDimsStableFramesRef.current++;
          } else {
            candidateRemoteDimsRef.current = { w: rawW, h: rawH };
            remoteDimsStableFramesRef.current = 1;
          }

          if (remoteDimsStableFramesRef.current >= 3) {
            const committed = lastKnownRemoteDimsRef.current;

            if (!committed) {
              // First commit: always accept
              lastKnownRemoteDimsRef.current = { w: rawW, h: rawH };
            } else {
              // Later commits: accept only if aspect ratio is extremely close
              const oldAR = committed.w / committed.h;
              const newAR = rawW / rawH;
              const relDelta = Math.abs(newAR - oldAR) / oldAR;
              const AR_THRESHOLD = 0.03; // 3%

              if (relDelta <= AR_THRESHOLD) {
                lastKnownRemoteDimsRef.current = { w: rawW, h: rawH };
              }
              // else: ignore this new orientation/aspect jump, keep using committed dims
            }
          }
        } else {
          // don't reset lastKnownRemoteDimsRef; just ignore this frame's junk dims
          remoteDimsStableFramesRef.current = 0;
        }

        const stable = lastKnownRemoteDimsRef.current;
        const videoW = stable?.w ?? (rawW || W);
        const videoH = stable?.h ?? (rawH || H);

        // 🔒 LOCK CANVAS DIMENSIONS AT START OF RECORDING
        // This prevents glitches when orientation changes during recording
        // The locked dimensions ensure consistent video output even if device rotates
        if (canvas && !initialCanvasDimensionsRef.current) {
          // First frame: lock the dimensions
          initialCanvasDimensionsRef.current = {
            width: canvas.width,
            height: canvas.height
          };
          console.log(`📹 [Recording] 🔒 Locked canvas at: ${canvas.width}x${canvas.height}`);
        }
        // Don't allow canvas resizing during recording - keeps dimensions stable
        // Video will be letterboxed/pillarboxed if orientation changes mid-recording
        // ✅ This is intentional: prevents codec issues and maintains quality

        // Use locked canvas dimensions for drawing
        const canvasW = canvas.width;
        const canvasH = canvas.height;

        // logging for recording, canvas issues
        if (Math.random() < 0.02) {
          console.log("📐 [Recording] remote dims", {
            raw: { w: rawW, h: rawH },
            stable,
            canvas: { w: canvasW, h: canvasH },
          });
        }
        

        // Check if remote video is temporarily unavailable (e.g. during camera flip)
        const remoteReady = remoteEl && remoteEl.readyState >= 2;

        if (!remoteReady) {
          // ✅ FIX: During camera flip, hold the last good frame instead of drawing black
          // This prevents the recording from showing blank frames during the switch
          if (lastGoodFrame) {
            ctx.putImageData(lastGoodFrame, 0, 0);
          }
          // Don't clear canvas - keep showing whatever was last drawn
          drawAnimRef.current = requestAnimationFrame(draw);
          return;
        }

        // Clear canvas before drawing (prevents ghosting)
        ctx.clearRect(0, 0, canvasW, canvasH);

        // "Cover" logic: fill canvas, crop overflow, maintain aspect ratio
        const scale = Math.max(canvasW / videoW, canvasH / videoH);
        const drawW = videoW * scale;
        const drawH = videoH * scale;
        const drawX = (canvasW - drawW) / 2;
        const drawY = (canvasH - drawH) / 2;

        // Apply remote orientation correction (guest rotated device - rotate to upright)
        const remoteAngle = remoteOrientationRef?.current ?? 0;
        if (remoteAngle !== 0) {
          const cx = drawX + drawW / 2;
          const cy = drawY + drawH / 2;
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate((-remoteAngle * Math.PI) / 180);
          ctx.translate(-cx, -cy);
          ctx.drawImage(remoteEl, drawX, drawY, drawW, drawH);
          ctx.restore();
        } else {
          ctx.drawImage(remoteEl, drawX, drawY, drawW, drawH);
        }

        // Draw local video (PIP) - scale based on current canvas size
        if (localEl && localEl.readyState >= 2) {
          const pipW = Math.round(canvasW * 0.25);
          const pipH = Math.round(
            (pipW / (localEl.videoWidth || 16)) * (localEl.videoHeight || 9)
          );
          const x = canvasW - pipW - 16;
          const y = canvasH - pipH - 16;
          ctx.save();
          ctx.beginPath();
          const r = 8;
          ctx.moveTo(x + r, y);
          ctx.lineTo(x + pipW - r, y);
          ctx.quadraticCurveTo(x + pipW, y, x + pipW, y + r);
          ctx.lineTo(x + pipW, y + pipH - r);
          ctx.quadraticCurveTo(x + pipW, y + pipH, x + pipW - r, y + pipH);
          ctx.lineTo(x + r, y + pipH);
          ctx.quadraticCurveTo(x, y + pipH, x, y + pipH - r);
          ctx.lineTo(x, y + r);
          ctx.quadraticCurveTo(x, y, x + r, y);
          ctx.closePath();
          ctx.clip();
          // Apply host orientation correction for PIP
          const hostAngle = hostOrientationRef?.current ?? 0;
          if (hostAngle !== 0) {
            const cx = x + pipW / 2;
            const cy = y + pipH / 2;
            ctx.translate(cx, cy);
            ctx.rotate((-hostAngle * Math.PI) / 180);
            ctx.translate(-cx, -cy);
          }
          ctx.drawImage(localEl, x, y, pipW, pipH);
          ctx.restore();
        }

        // Save the last good frame for use during camera flip interruptions
        try {
          lastGoodFrame = ctx.getImageData(0, 0, canvasW, canvasH);
        } catch {
          // getImageData can fail on tainted canvases - ignore
        }
      } catch (err) {
        // Log errors for debugging
        console.warn('⚠️ [Recording] Draw frame error:', err);
      }
      drawAnimRef.current = requestAnimationFrame(draw);
    };
    drawAnimRef.current = requestAnimationFrame(draw);

    const videoTrack = canvas.captureStream(30).getVideoTracks()[0];

    const ac = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    audioCtxRef.current = ac;
    const dest = ac.createMediaStreamDestination();
    audioDestRef.current = dest;

    try {
      const lStream = localStreamRef.current;
      if (lStream && lStream.getAudioTracks().length) {
        const lsrc = ac.createMediaStreamSource(lStream);
        lsrc.connect(dest);
      }
    } catch {}

    try {
      const rStream = remoteVideoRef.current?.srcObject as MediaStream | null;
      if (rStream && rStream.getAudioTracks().length) {
        const rsrc = ac.createMediaStreamSource(rStream);
        rsrc.connect(dest);
      }
    } catch {}

    const mixed = new MediaStream();
    if (videoTrack) mixed.addTrack(videoTrack);
    dest.stream.getAudioTracks().forEach((t) => mixed.addTrack(t));
    mixedStreamRef.current = mixed;
    return mixed;
  };

  const cleanupMixer = () => {
    if (drawAnimRef.current) cancelAnimationFrame(drawAnimRef.current);
    drawAnimRef.current = null;
    try {
      audioCtxRef.current?.close();
    } catch {}
    audioCtxRef.current = null;
    audioDestRef.current = null;
    mixCanvasRef.current = null;
    mixedStreamRef.current = null;
    // 🔓 Unlock canvas dimensions for next recording
    initialCanvasDimensionsRef.current = null;
    console.log('📹 [Recording] 🔓 Canvas dimensions unlocked');
  };

  const pickRecorderType = () => {
    // Prioritize MP4 for better mobile compatibility
    const candidates = [
      {
        mime: "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
        ext: "mp4",
        content: "video/mp4",
      },
      { mime: "video/webm;codecs=vp9,opus", ext: "webm", content: "video/webm" },
      { mime: "video/webm;codecs=vp8,opus", ext: "webm", content: "video/webm" },
    ];
    if (typeof MediaRecorder === "undefined")
      return { mime: "", ext: "mp4", content: "video/mp4" };
    for (const c of candidates) {
      try {
        if (MediaRecorder.isTypeSupported(c.mime)) return c;
      } catch {}
    }
    return { mime: "", ext: "mp4", content: "video/mp4" };
  };

  // ✅ FIX: Wrap in useCallback to prevent auto-start timer from being cleared
  const startRecording = useCallback(async () => {
    console.log('🎬 startRecording called', {
      isHost,
      useMediasoup: USE_MEDIASOUP,
      hasExistingRecorder: !!mediaRecorderRef.current,
      hasRemoteVideo: !!remoteVideoRef.current,
      hasLocalVideo: !!localVideoRef.current,
      remoteVideoSrcObject: !!remoteVideoRef.current?.srcObject,
      remoteVideoReadyState: remoteVideoRef.current?.readyState,
    });

    if (!isHost) {
      console.error('❌ Not host, cannot record');
      return;
    }
    if (USE_MEDIASOUP_SERVER_RECORDING && isRecording) {
      console.warn('⚠️ Already recording (server-side)');
      return;
    }
    if (mediaRecorderRef.current) {
      console.warn('⚠️ Already recording');
      return;
    }

    // Optional server-side recording (mediasoup). Default path is client-side dynamic recorder
    // so orientation changes are preserved exactly like the live call UI.
    if (USE_MEDIASOUP_SERVER_RECORDING) {
      setIsRecording(true);
      sessionStorage.setItem(`recording_active_${roomId}`, 'true');
      sessionStorage.setItem(`recording_start_${roomId}`, String(Date.now()));
      socketRef.current?.emit("recording-state", {
        roomId,
        isRecording: true,
        senderId: socketRef.current?.id,
        serverSide: true,
      });
      try {
        await apiClient.post('/update-call', {
          roomId,
          updates: {
            recording_status: "recording",
            recording_started_at: new Date().toISOString(),
          }
        });
      } catch (error) {
        console.error('❌ Failed to update recording status:', error);
      }
      // showImportantToast("Recording started (server-side)", "recording");
      return;
    }

    // ✅ CRITICAL FIX: Set recording state IMMEDIATELY to show UI
    // This ensures the recording indicator shows even while waiting for stream to build
    console.log('🎬 Setting recording state to true (UI will update immediately)');
    setIsRecording(true);

    // ✅ CRITICAL FIX: Add retry logic for building mixed stream
    // Remote video might not be ready immediately when auto-start triggers
    console.log('🎬 Building mixed stream (guest + host video/audio)...');
    let mixed: MediaStream | null = null;
    const maxRetries = 10; // Increased from 5 to 10
    const retryDelay = 1500; // Increased from 1000ms to 1500ms

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      console.log(`🔄 [Recording] Attempt ${attempt}/${maxRetries} to build mixed stream`);

      mixed = buildMixedStream();

      if (mixed) {
        console.log(`✅ [Recording] Mixed stream created successfully on attempt ${attempt}`);
        break;
      }

      if (attempt < maxRetries) {
        console.log(`⏳ [Recording] Waiting ${retryDelay}ms before retry ${attempt + 1}...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }

    if (!mixed) {
      console.error('❌ Failed to build mixed stream after all retries');
      // Reset recording state since we failed
      setIsRecording(false);
      return;
    }

    console.log('✅ Mixed stream created:', {
      videoTracks: mixed.getVideoTracks().length,
      audioTracks: mixed.getAudioTracks().length,
      tracks: mixed.getTracks().map(t => ({
        kind: t.kind,
        label: t.label,
        enabled: t.enabled
      }))
    });

    recordingChunksRef.current = [];
    recordingStartRef.current = Date.now();

    // ✅ FIX: Persist recording state in sessionStorage so refresh doesn't reset count
    recordingNumberRef.current += 1;
    sessionStorage.setItem(`recording_num_${roomId}`, String(recordingNumberRef.current));
    sessionStorage.setItem(`recording_active_${roomId}`, 'true');
    sessionStorage.setItem(`recording_start_${roomId}`, String(Date.now()));
    console.log(`📹 [Recording] Recording #${recordingNumberRef.current} starting`);

    // restore nay previously locked canvas size 
    // const storedCanvas = sessionStorage.getItem(`recording_canvas_${roomId}`);
    // if (storedCanvas) {
    //   try {
    //     lockedCanvasDimsRef.current = JSON.parse(storedCanvas);
    //     console.log("📹 [Recording] Restored locked canvas:", lockedCanvasDimsRef.current);
    //   } catch {}
    // }

    const recType = pickRecorderType();
    console.log('🎙️ Recording type selected:', recType);

    // ✅ FIX: Configure video bitrate to maintain quality during orientation changes
    const videoTrack = mixed.getVideoTracks()[0];
    const settings = videoTrack?.getSettings() || {};
    const videoBitrate = Math.max(2500000, (settings.width || 1280) * (settings.height || 720) * 30 * 0.1); // ~2.5Mbps minimum

    const options = recType.mime
      ? ({ mimeType: recType.mime, videoBitsPerSecond: videoBitrate } as any)
      : ({ videoBitsPerSecond: videoBitrate } as any);

    const mr = new MediaRecorder(mixed, options);
    console.log('📹 MediaRecorder created with options:', { ...options, videoBitrate });

    mr.ondataavailable = (e) => {
      if (e.data && e.data.size) recordingChunksRef.current.push(e.data);
    };

    // 🔚 NEW onstop: upload via backend proxy (SECURE - no tokens exposed to client)
    mr.onstop = async () => {
      let blob: Blob | null = null;
      let durationSec: number | null = null;
      try {
        console.log('⏹️ Recording stopped, processing upload via backend proxy...');
        console.log('📊 Recording chunks collected:', recordingChunksRef.current.length);

        // ✅ Check if we have any data to upload
        if (recordingChunksRef.current.length === 0) {
          console.warn('⚠️ No recording chunks collected - recording may have been too short');
          return;
        }

        blob = new Blob(recordingChunksRef.current, {
          type: recType.content,
        });

        console.log('📦 Recording blob created:', {
          size: blob.size,
          sizeInMB: (blob.size / 1024 / 1024).toFixed(2),
          type: blob.type
        });

        // ✅ Check if blob has meaningful data
        if (blob.size < 1000) { // Less than 1KB
          console.warn('⚠️ Recording blob too small:', blob.size, 'bytes');
          return;
        }

        durationSec = recordingStartRef.current
          ? Math.max(
              1,
              Math.round((Date.now() - recordingStartRef.current) / 1000)
            )
          : null;

        console.log('☁️ Uploading recording...', {
          roomId,
          duration: durationSec,
          blobSize: blob.size,
          sizeInMB: (blob.size / 1024 / 1024).toFixed(2),
          mimeType: recType.content
        });

        const fileSizeMB = blob.size / 1024 / 1024;
        const isLargeFile = blob.size > CHUNKED_UPLOAD_THRESHOLD;

        console.log('📤 ========================================');
        console.log(`📤 UPLOADING RECORDING (${fileSizeMB.toFixed(1)}MB)`);
        console.log(`📤 Method: ${isLargeFile ? 'PROGRESS-TRACKED (large file)' : 'STANDARD'}`);
        console.log('📤 ========================================');

        // Step 1: Get signed upload URL from backend
        console.log('🔗 Step 1: Requesting signed upload URL...');
        setUploadProgress(0);

        const urlResponse = await apiClient.post<{
          ok: boolean;
          signedUrl?: string;
          token?: string;
          storagePath?: string;
          publicUrl?: string;
          error?: string;
        }>('/get-upload-url', {
          roomId,
          extension: recType.ext,
          mimeType: recType.content,
          type: 'recording'
        });

        if (!urlResponse.ok || !urlResponse.signedUrl) {
          throw new Error(urlResponse.error || 'Failed to get upload URL');
        }

        console.log('✅ Got signed URL:', {
          storagePath: urlResponse.storagePath,
          publicUrl: urlResponse.publicUrl
        });

        // Step 2: Upload directly to Supabase Storage using signed URL
        // Use XMLHttpRequest with progress tracking for ALL files (better reliability)
        console.log('📤 Step 2: Uploading to Supabase Storage with progress tracking...');
        const uploadStartTime = Date.now();

        await uploadWithRetries(
          urlResponse.signedUrl,
          blob,
          recType.content,
          (percent) => {
            setUploadProgress(percent);
            // Update toast every 10%
            if (isLargeFile && percent % 10 === 0 && percent > 0) {
              console.log(`📤 Upload ${percent}% complete`);
            }
          }
        );

        const uploadDuration = ((Date.now() - uploadStartTime) / 1000).toFixed(1);
        console.log(`✅ Upload complete in ${uploadDuration}s`);
        setUploadProgress(100);

        // Step 3: Confirm upload and save metadata
        console.log('📝 Step 3: Confirming upload and saving metadata...');
        const confirmResponse = await apiClient.post<{
          ok: boolean;
          recordingId?: string;
          storagePath?: string;
          publicUrl?: string;
          error?: string;
          warning?: string;
        }>('/confirm-upload', {
          roomId,
          storagePath: urlResponse.storagePath,
          mimeType: recType.content,
          durationSeconds: durationSec,
          fileSizeBytes: blob.size,
          type: 'recording'
        });

        if (!confirmResponse.ok) {
          console.warn('⚠️ Upload confirmed but metadata save failed:', confirmResponse.error);
        }

        console.log('✅ ========================================');
        console.log('✅ RECORDING UPLOAD COMPLETE');
        console.log('✅ ========================================');
        console.log('✅ Recording ID:', confirmResponse.recordingId);
        console.log('✅ Storage path:', confirmResponse.storagePath || urlResponse.storagePath);
        console.log('✅ Public URL:', confirmResponse.publicUrl || urlResponse.publicUrl);
        console.log('✅ File size:', fileSizeMB.toFixed(2), 'MB');
        console.log('✅ Upload time:', uploadDuration, 's');
        console.log('✅ Upload speed:', (fileSizeMB / parseFloat(uploadDuration)).toFixed(2), 'MB/s');

        if (confirmResponse.warning) {
          console.warn('⚠️ Warning:', confirmResponse.warning);
        }

        setUploadProgress(null);
      } catch (e: any) {
        console.error('❌ ========================================');
        console.error('❌ RECORDING UPLOAD FAILED');
        console.error('❌ ========================================');
        console.error('❌ Error:', e?.message || String(e));
        console.error('❌ Stack:', e?.stack);
        console.error('❌ Room ID:', roomId);
        console.error('❌ Blob size:', blob?.size ? `${(blob.size / 1024 / 1024).toFixed(2)}MB` : 'unknown');
        console.error('❌ Duration:', durationSec, 'seconds');

        setUploadProgress(null);

        // Update status to failed via backend API
        try {
          await apiClient.post('/update-call', {
            roomId,
            updates: {
              recording_status: "failed",
              recording_ended_at: new Date().toISOString(),
            }
          });
          console.log('✅ Updated call_sessions with recording_status=failed');
        } catch (failedUpdateError) {
          console.error('❌ Failed to update recording status to failed:', failedUpdateError);
        }
      } finally {
        setUploadProgress(null);
        cleanupMixer();
      }
    };

    mediaRecorderRef.current = mr;
    const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    mr.start(isiOS ? undefined : 1000);
    // ✅ Recording state already set to true at the beginning of function (line 213)
    // This ensures UI updates immediately, even during mixed stream retry loop
    console.log('🎬 MediaRecorder started successfully');
    socketRef.current?.emit("recording-state", {
      roomId,
      isRecording: true,
      senderId: socketRef.current?.id,
      serverSide: USE_MEDIASOUP_SERVER_RECORDING,
    });

    // Update call_sessions table with recording status via backend API
    try {
      await apiClient.post('/update-call', {
        roomId,
        updates: {
          recording_status: "recording",
          recording_started_at: new Date().toISOString(),
        }
      });
      console.log('✅ Updated call_sessions with recording_status=recording');
    } catch (error) {
      console.error('❌ Failed to update recording status:', error);
    }

    // Show important toast for recording start
    showImportantToast("Recording started", "recording");
  }, [isHost, roomId, isRecording, socketRef, remoteVideoRef, localVideoRef, localStreamRef, baseUrl, toast]); // ✅ Add dependencies for useCallback

  const stopRecording = async (): Promise<void> => {
    if (!isHost) {
      console.warn('⚠️ Not host, ignoring stopRecording');
      return;
    }
    // Optional server-side recording (mediasoup)
    if (USE_MEDIASOUP_SERVER_RECORDING) {
      if (!isRecording) return;
      setIsRecording(false);
      sessionStorage.removeItem(`recording_active_${roomId}`);
      socketRef.current?.emit("recording-state", {
        roomId,
        isRecording: false,
        senderId: socketRef.current?.id,
        serverSide: true,
      });
      showImportantToast("Recording stopped (uploading...)", "recording");
      return;
    }

    const mr = mediaRecorderRef.current;
    if (!mr) return;

    // ✅ FIX: Return a Promise that resolves when upload completes
    return new Promise<void>((resolve) => {
      const originalOnStop = mr.onstop;

      // Wrap the original onstop to resolve the promise when done
      mr.onstop = async (event) => {
        try {
          // Call the original onstop handler (which does the upload)
          if (originalOnStop) {
            await originalOnStop.call(mr, event);
          }
        } finally {
          // Always resolve, even if upload fails
          resolve();
        }
      };

      try {
        mr.stop();
      } catch {
        // If stop fails, resolve immediately
        resolve();
      }

      mediaRecorderRef.current = null;
      setIsRecording(false);
      // ✅ FIX: Clear recording active flag from sessionStorage
      sessionStorage.removeItem(`recording_active_${roomId}`);

      socketRef.current?.emit("recording-state", {
        roomId,
        isRecording: false,
        senderId: socketRef.current?.id,
        serverSide: USE_MEDIASOUP_SERVER_RECORDING,
      });
    });
  };

  /**
   * ✅ FIX: Emergency save - uploads whatever recording data we have so far
   * Called from beforeunload/pagehide when user is about to leave the page
   * Uses synchronous blob creation + sendBeacon for fire-and-forget upload
   */
  const emergencySave = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (!mr || recordingChunksRef.current.length === 0) {
      console.log('📹 [Recording] No data to emergency-save');
      return;
    }

    console.log('🚨 [Recording] EMERGENCY SAVE - saving recording chunks before page unload');

    // Force the MediaRecorder to flush its buffer
    try {
      mr.stop();
    } catch {}

    // Build blob from whatever chunks we have
    const recType = pickRecorderType();
    const blob = new Blob(recordingChunksRef.current, { type: recType.content });

    if (blob.size < 1000) {
      console.warn('⚠️ [Recording] Emergency blob too small, skipping:', blob.size);
      return;
    }

    const durationSec = recordingStartRef.current
      ? Math.max(1, Math.round((Date.now() - recordingStartRef.current) / 1000))
      : null;

    console.log(`🚨 [Recording] Emergency save: ${(blob.size / 1024 / 1024).toFixed(1)}MB, ${durationSec}s`);

    // Use sendBeacon to send metadata to backend (browser guarantees delivery even during unload)
    // The actual blob is too large for sendBeacon (limit ~64KB payload), so we store it in IndexedDB
    // and the backend can retrieve it later, OR we just notify the backend that recording was interrupted
    try {
      const payload = JSON.stringify({
        roomId,
        event: 'recording-interrupted',
        durationSeconds: durationSec,
        chunkCount: recordingChunksRef.current.length,
        blobSizeBytes: blob.size,
        timestamp: new Date().toISOString(),
      });
      navigator.sendBeacon(`${baseUrl}/api/update-call`, payload);
      console.log('✅ [Recording] Emergency beacon sent');
    } catch (err) {
      console.error('❌ [Recording] sendBeacon failed:', err);
    }

    // Store recording state so we know it was interrupted
    sessionStorage.setItem(`recording_interrupted_${roomId}`, 'true');
    sessionStorage.removeItem(`recording_active_${roomId}`);

    // Clean up
    mediaRecorderRef.current = null;
    cleanupMixer();
  }, [roomId, baseUrl]);

  const toggleRecording = useCallback(() => {
    if (isRecording) stopRecording();
    else startRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  return {
    isRecording,
    setIsRecording, // Export setter for external state updates (e.g., from socket events)
    uploadProgress, // null = not uploading, 0-100 = upload percentage
    startRecording,
    stopRecording,
    toggleRecording,
    emergencySave, // ✅ Export for use in beforeunload handler
    recordingNumberRef, // ✅ Export for recording count display
  };
}
